import { HttpClient, HttpHeaders, HttpResponse, HttpUserEvent } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import dayjs from 'dayjs';

import { Reboot } from '../../../reboot';
import { AuthMethodModel } from '../models/auth-method.model';

import { AuthenticationModel } from '../models/authentication.model';

export const ACCESS_TOKEN_EXPIRATION_NAME: string = 'auth.expires_at';
export const ACCESS_TOKEN_NAME: string = 'auth.access_token';
export const REFRESH_TOKEN_NAME: string = 'auth.refresh_token';
export const AUTO_TOKEN_REFRESH: string = 'auth.auto_token_refresh';

const API_ENDPOINT = '/oauth/';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  public isAuthenticatedSubject: BehaviorSubject<boolean>;
  public redirectUrl: string;

  private code: string = null;
  private provider: string = null;

  /**
   * The Constructor for the Authentication Service.
   *
   * @param {HttpClient} http The HttpClient
   * @param {NgZone} ngZone
   * @param {Router} router
   */
  public constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private router: Router,
  ) {
    this.isAuthenticatedSubject = new BehaviorSubject<boolean>(!AuthService.isTokenExpired());
  }

  /**
   * Setter for the code property
   *
   * @param {string | null} code
   *
   * @return void
   */
  public set authCode(code: string|null) {
    this.code = code;
  }

  /**
   * Setter for the provider property
   *
   * @param {string | null} provider
   *
   * @return void
   */
  public set authProvider(provider: string|null) {
    this.provider = provider;
  }

  /**
   * Returns the current expiration timestamp on the access token (if set) as Moment object.
   * @return The Moment date for the token expiration if it is set.
   */
  public static getTokenExpirationDate(): dayjs.Dayjs | null {
    const expiresAt = JSON.parse(localStorage.getItem(ACCESS_TOKEN_EXPIRATION_NAME));
    return expiresAt ? dayjs(expiresAt) : null;
  }

  /**
   * Check whether the access token for the player has expired (and, thus, is invalid).
   * @return boolean Whether the current access token has expired.
   */
  public static isTokenExpired(): boolean {
    const tokenExpirationDate = AuthService.getTokenExpirationDate();
    return tokenExpirationDate
      ? dayjs().isAfter(tokenExpirationDate)
      : true;
  }

  /**
   * Return whether the player has a valid access token (i.e., is authenticated).
   * @return string { The access token for the current authenticated player }.
   */
  public static getAccessToken(): string {
    return localStorage.getItem(ACCESS_TOKEN_NAME) || null;
  }

  /**
   * Return whether the player has a valid refresh token (i.e., is authenticated).
   * @return string { The refresh token for the current authenticated player }.
   */
  public static getRefreshToken(): string {
    return localStorage.getItem(REFRESH_TOKEN_NAME) || null;
  }

  /**
   * First, checks whether the access token for the user has not expired.
   * Returns an Observable based on the authentication state of the player.
   *
   * @return {Observable<boolean>} The Authentication Observable.
   */
  public static isAuthenticated(): Observable<boolean> {
    const currentAccessToken = AuthService.getAccessToken();
    return of(currentAccessToken !== null);
  }

  /**
   * Returns the current authentication status.
   * @return Observable isAuthenticatedSubject
   */
  public getPlayerAuthenticationStatus() {
    return this.isAuthenticatedSubject.asObservable();
  }

  /**
   * Handling the login with username and password
   *
   * @param {string} username
   * @param {string} password
   * @param {boolean} refreshToken
   *
   * @return {Observable<AuthenticationModel>}
   */
  public doFormLogin(username: string, password: string, refreshToken: boolean): Observable<AuthenticationModel> {
    // Set or remove the remembered username in the local storage depending on the player's preference on the login form.
    refreshToken ? localStorage.setItem(AUTO_TOKEN_REFRESH, username) : localStorage.removeItem(AUTO_TOKEN_REFRESH);

    const credentials = {
      username: username,
      password: password,
      grant_type: 'password',
    };

    const url = 'token/form';

    return this.doLogin(credentials, url);
  }

  /**
   * Handling the login through an identity provider
   *
   * @return {Observable<AuthenticationModel>}
   */
  public doSocialLogin(): Observable<AuthenticationModel> {
    const credentials = {
      code: this.code,
      provider: this.provider,
      grant_type: 'social_grant',
      origin: 'game',
    };

    const url = 'token/oauth2';

    return this.doLogin(credentials, url).pipe(
      finalize(() => {
        this.authCode = null;
        this.authProvider = null;
      }),
    );
  }

  /**
   * Handling the login through an identity provider
   *
   * @return {Observable<AuthenticationModel>}
   */
  public doSaml2Login(): Observable<AuthenticationModel> {
    const credentials = {
      code: this.code,
      provider: this.provider,
      grant_type: 'saml2_grant',
      origin: 'game',
    };

    const url = 'token/saml2';

    return this.doLogin(credentials, url).pipe(
      finalize(() => {
        this.authCode = null;
        this.authProvider = null;
      }),
    );
  }

  /**
   * Handling the login through an identity provider
   *
   * @return {Observable<AuthenticationModel>}
   */
  public doLtiLogin(): Observable<AuthenticationModel> {
    const credentials = {
      code: this.code,
      provider: this.provider,
      grant_type: 'lti_grant',
      origin: 'game',
    };

    const url = 'token/lti';

    return this.doLogin(credentials, url).pipe(
      finalize(() => {
        this.authCode = null;
        this.authProvider = null;
      }),
    );
  }

  /**
   * Attempts to log the player into the game, based on provided credentials.
   *
   * @return {Observable<AuthenticationModel>}
   */
  public doLogin(credentials: Object, url: string): Observable<AuthenticationModel> {

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    };

    return this.http.post<AuthenticationModel>(API_ENDPOINT + url, credentials, httpOptions).pipe(
      map((authResult: AuthenticationModel) => {
        if (authResult.access_token) {
          this.updateAccessToken(
            authResult.access_token,
            authResult.refresh_token,
            authResult.expires_in,
          );
        }

        return authResult;
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Attempts to refresh the player's access token, based on the refresh_token grant type.
   *
   * @return Observable<any>
   */
  public refreshAccessToken(): Observable<any> {

    const httpOptions = {
      withCredentials: true,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(API_ENDPOINT + 'token/refresh', {
      refresh_token: AuthService.getRefreshToken(),
      grant_type: 'refresh_token',
    }, httpOptions).pipe(
      tap((authResult: AuthenticationModel) => {
        if (authResult.access_token) {
          this.updateAccessToken(
            authResult.access_token,
            authResult.refresh_token,
            authResult.expires_in,
          );
        }
      }),
      catchError(() => {
        return throwError(() => new Error('Failed to refresh the token, the refresh token probably expired'));
      }),
    );
  }

  /**
   * Logs the player out of the game by removing references to the access token.
   */
  public doLogout(): Observable<HttpResponse<any> | HttpUserEvent<any> | any> {

    const token = localStorage.getItem(ACCESS_TOKEN_NAME);
    const httpOptions = {
      withCredentials: true,
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http.delete(API_ENDPOINT + 'logout', httpOptions).pipe(
      finalize(() => {
        return this.clearAccessTokenAndRestartApp();
      }),
    );
  }

  /**
   * Api call to get the available methods for authentication
   *
   * @return {Observable<AuthMethodModel[]>}
   */
  public getAllowedAuthMethods(): Observable<AuthMethodModel[]> {
    return this.http.get('/oauth/get/auth/methods').pipe(
      shareReplay(1),
      map((response: {auth_methods: AuthMethodModel[]}) => {
        return response.auth_methods;
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Clear the accessToken related info in the local storage and reboots the app
   *
   * @return void
   */
  public clearAccessTokenAndRestartApp(): void {
    this.clearAccessToken();

    // Triggers the reboot in main.ts - essentially reinitializing the app.
    this.ngZone.runOutsideAngular(() => {
      Reboot.getBootControl().restart();
    });
  }

  /**
   * Clear the accessToken related info in the local storage
   */
  public clearAccessToken(): void {
    localStorage.removeItem(ACCESS_TOKEN_EXPIRATION_NAME);
    localStorage.removeItem(ACCESS_TOKEN_NAME);
    localStorage.removeItem(REFRESH_TOKEN_NAME);

    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Updates the access token and its expiration date.
   * @param accessToken The access token string
   * @param refreshToken
   * @param expiresIn The expiration time in seconds
   */
  private updateAccessToken(accessToken: string, refreshToken: string, expiresIn: number) {
    const expiresAt = dayjs().add(expiresIn, 'second');
    localStorage.setItem(ACCESS_TOKEN_EXPIRATION_NAME, JSON.stringify(expiresAt.valueOf()));
    localStorage.setItem(ACCESS_TOKEN_NAME, accessToken);
    localStorage.setItem(REFRESH_TOKEN_NAME, refreshToken);
    this.isAuthenticatedSubject.next(!AuthService.isTokenExpired());
  }

}
